import { createReducer, createActions } from 'reduxsauce'
import {CommonReducer, Reducer} from '../Utils/reduxHelpers'
import { Action } from 'redux'
import { ReducerTypes } from './utils'
import {CardPaymentStatusType, CardPaymentType} from '../Components/SelfService/Types'
import * as paymentTerminalActionTypes from '../lib/payment-terminal/actionTypes'
import * as actionTypes from '../lib/payment-terminal/actionTypes'

type ActionTypes = {
    CREATE_PAYMENT: string
    CREATE_PAYMENT_FAILURE: string
    CREATE_CARD_PAYMENT: string
    CREATE_CARD_PAYMENT_FAILURE: string
    CREATE_CARD_PAYMENT_SUCCESS: string
    ABORT_CARD_PAYMENT: string
    CARD_PAYMENT_COMPLETED: string
}

const actions = {
    createPayment: ['token', 'quick'],
    createPaymentFailure: null,
    createCardPayment: ['token', 'language'],
    createCardPaymentFailure: null,
    createCardPaymentSuccess: ['cardPayment', 'cardToken'],
    abortCardPayment: null,
    cardPaymentCompleted: ['cardPayment'],
}

type AnyCreators = {
    [key in keyof typeof actions]: (...params: any[]) => Action<any>
}

// Override AnyCreators with strong types
interface Creators extends AnyCreators {
    createPayment: (token: string, quick?: boolean) => Action<any>
    handlePaymentResult: (result: string, query: string) => Action<any>
}

const { Types, Creators } = createActions<ActionTypes, Creators>(actions)

export const PaymentTypes = Types
export default Creators

export interface PaymentState {
    cardPayment: CardPaymentType | null
    cardPaymentStatus: CardPaymentStatusType
    cardToken: string | null
    fetching: {
        payment: boolean
    }
}

export const INITIAL_STATE: PaymentState = {
    cardPayment: null,
    cardPaymentStatus: {
        isAborting: false,
        isProcessing: false,
        isCompleted: false,
        displayMessage: null,
    },
    cardToken: null,
    fetching: {
        payment: false,
    },
}

type CommonFetchingType = keyof PaymentState['fetching']

const commonFetching = (
    type: CommonFetchingType,
): CommonReducer<PaymentState> => (state: PaymentState) => ({
    ...state,
    fetching: {
        ...state.fetching,
        [type]: true,
    },
})

const commonFailure = (
    type: CommonFetchingType,
): CommonReducer<PaymentState> => (state: PaymentState) => ({
    ...state,
    fetching: {
        ...state.fetching,
        [type]: false,
    },
})

const PAYMENT = 'payment'

const resetState: CommonReducer<PaymentState> = (_) => ({...INITIAL_STATE})

const setDisplayMessage: Reducer<any> = (state: PaymentState, {message}) => ({
    ...state,
    cardPaymentStatus: {
        ...state.cardPaymentStatus,
        displayMessage: message?.message ?? null,
    },
})

export const createPayment: CommonReducer<PaymentState> = commonFetching(
    PAYMENT,
)
export const createPaymentFailure: CommonReducer<PaymentState> = commonFailure(
    PAYMENT,
)

export const createCardPayment: CommonReducer<PaymentState> = (state: PaymentState) => ({
    ...state,
    cardPayment: null,
    cardToken: null,
    cardPaymentStatus: {
        isAborting: false,
        isProcessing: true,
        isCompleted: false,
        displayMessage: null,
    },
    fetching: { ...state.fetching, [PAYMENT]: true },
})
export const createCardPaymentFailure: CommonReducer<PaymentState> = (state: PaymentState) => ({
    ...state,
    cardPayment: null,
    cardToken: null,
    cardPaymentStatus: {
        isAborting: false,
        isProcessing: false,
        isCompleted: false,
        displayMessage: null,
    },
    fetching: { ...state.fetching, [PAYMENT]: false },
})
export const createCardPaymentSuccess: Reducer<PaymentState> = (state: PaymentState, { cardPayment, cardToken }) => ({
    ...state,
    cardPayment,
    cardToken,
    cardPaymentStatus: {
        isAborting: false,
        isProcessing: true,
        isCompleted: false,
        displayMessage: null,
    },
    fetching: { ...state.fetching, [PAYMENT]: false },
})
export const abortCardPayment: Reducer<PaymentState> = (state: PaymentState) => ({
    ...state,
    cardPaymentStatus: {
        ...state.cardPaymentStatus,
        isAborting: true,
    },
})
export const cardPaymentCompleted: Reducer<PaymentState> = (state: PaymentState, { cardPayment }) => ({
    ...state,
    cardPayment: cardPayment,
    cardPaymentStatus: {
        isAborting: false,
        isProcessing: false,
        isCompleted: true,
        displayMessage: state.cardPaymentStatus.displayMessage
    },
})

type CheckInReducerTypes = ReducerTypes<PaymentState, ActionTypes>

export const reducerTypes: CheckInReducerTypes = {
    [Types.CREATE_PAYMENT]: createPayment,
    [Types.CREATE_PAYMENT_FAILURE]: createPaymentFailure,
    [Types.CREATE_CARD_PAYMENT]: createCardPayment,
    [Types.CREATE_CARD_PAYMENT_FAILURE]: createCardPaymentFailure,
    [Types.CREATE_CARD_PAYMENT_SUCCESS]: createCardPaymentSuccess,
    [Types.ABORT_CARD_PAYMENT]: abortCardPayment,
    [Types.CARD_PAYMENT_COMPLETED]: cardPaymentCompleted,
    [paymentTerminalActionTypes.PAYMENT_TERMINAL_DISCONNECTED]: resetState,
    [actionTypes.PAYMENT_TERMINAL_DISPLAY_MESSAGE_RECEIVED]: setDisplayMessage,
}

export const reducer = createReducer<PaymentState, Action<CheckInReducerTypes>>(
    INITIAL_STATE,
    reducerTypes,
)
